import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera,
  Sparkles,
  Loader2,
  RefreshCw,
  ChevronRight,
  ShieldCheck,
  Zap,
  Award,
  Clock,
  Info,
} from "lucide-react";

/**
 * PRO-PRE OS™ — AI Vision + AI Pricing
 * Public page. Upload a photo of a dirty textile → get a targeted quote
 * generated by GPT-5.4 vision, aligned to Pro-pre's price grid.
 */
export default function DevisInstant() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [imageBase64, setImageBase64] = React.useState("");
  const [imagePreview, setImagePreview] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [clientName, setClientName] = React.useState("");
  const [clientEmail, setClientEmail] = React.useState("");
  const [clientPhone, setClientPhone] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [dossier, setDossier] = React.useState(null);

  const readFile = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error(t("devis.err_size"));
      return;
    }
    // Resize down to max 1280px to save bandwidth + tokens
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      const b64 = c.toDataURL("image/jpeg", 0.85);
      setImageBase64(b64);
      setImagePreview(b64);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      toast.error(t("devis.err_image"));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const submit = async () => {
    if (!imageBase64) {
      toast.error(t("devis.err_photo_missing"));
      return;
    }
    setLoading(true);
    setDossier(null);
    try {
      const r = await api.post("/ai-vision/analyze", {
        image_base64: imageBase64,
        description,
        client_name: clientName,
        client_email: clientEmail || null,
        client_phone: clientPhone,
        lang: (i18n.language || "fr").slice(0, 2),
      });
      setDossier(r.data?.dossier);
      toast.success(t("devis.done"));
      setTimeout(() => {
        document.getElementById("devis-result")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("devis.err_generic");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section" data-testid="devis-page">
      <div className="container-narrow max-w-3xl">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-navy text-white text-xs px-3 py-1 font-semibold">
            <Zap className="h-3 w-3" /> PRO-PRE OS™ · AI Vision
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-navy">
            {t("devis.title")}
          </h1>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            {t("devis.subtitle")}
          </p>
        </div>

        {/* Upload zone */}
        <div className="mt-8 card-clean p-6" data-testid="devis-form">
          <label
            htmlFor="devis-photo"
            className="block cursor-pointer"
            data-testid="devis-photo-label"
          >
            <div
              className={`rounded-xl border-2 border-dashed transition ${
                imagePreview ? "border-[#5BA4D4]" : "border-slate-300 hover:border-[#5BA4D4]"
              } bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden`}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="w-full h-full object-contain" data-testid="devis-preview" />
              ) : (
                <div className="text-center px-4">
                  <Camera className="h-10 w-10 text-slate-400 mx-auto" />
                  <p className="mt-3 text-sm font-medium text-navy">
                    {t("devis.upload_cta")}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{t("devis.upload_hint")}</p>
                </div>
              )}
            </div>
            <input
              id="devis-photo"
              type="file"
              accept="image/*"
              onChange={(e) => readFile(e.target.files?.[0])}
              className="hidden"
              data-testid="devis-photo-input"
            />
          </label>

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t("devis.desc_placeholder")}
            className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#5BA4D4]/40 focus:border-[#5BA4D4] outline-none"
            data-testid="devis-desc"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder={t("devis.name")}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="devis-name"
            />
            <input
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              placeholder={t("devis.phone")}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="devis-phone"
            />
            <input
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              type="email"
              placeholder={t("devis.email")}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="devis-email"
            />
          </div>

          <button
            onClick={submit}
            disabled={loading || !imageBase64}
            className="btn-primary mt-5 w-full disabled:opacity-40"
            data-testid="devis-submit"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("devis.analyzing")}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {t("devis.analyze")}
              </>
            )}
          </button>

          <p className="mt-3 text-[11px] text-slate-500 text-center flex items-center justify-center gap-1">
            <ShieldCheck className="h-3 w-3" /> {t("devis.trust")}
          </p>
        </div>

        {/* Result dossier */}
        {dossier ? (
          <DossierCard dossier={dossier} onBook={() => navigate("/defi")} />
        ) : null}

        <p className="mt-8 text-center text-xs text-slate-400">
          <Link to="/reglement" className="hover:text-[#5BA4D4] underline">{t("devis.how_it_works")}</Link>
        </p>
      </div>
    </div>
  );
}

// ---------- Dossier presentation ----------
function DossierCard({ dossier, onBook }) {
  const { t } = useTranslation();
  const risk = dossier.cleaning_risk || "medium";
  const riskColor = { low: "text-emerald-600", medium: "text-amber-600", high: "text-red-600" }[risk] || "text-slate-600";
  const conf = Number(dossier.confidence_score) || 0;
  const confKey = conf >= 75 ? "high" : conf >= 50 ? "mid" : "low";
  const confClass = { high: "bg-emerald-100 text-emerald-700", mid: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700" }[confKey];
  const successPct = dossier.success_probability_percent ?? 0;
  const successBars = Math.max(0, Math.min(5, Math.round(successPct / 20)));
  const successKey = successPct >= 75 ? "high" : successPct >= 50 ? "mid" : "low";

  // Upsell / promo state
  const [selectedUpsells, setSelectedUpsells] = React.useState([]);
  const [promoCode, setPromoCode] = React.useState("");
  const [promoValid, setPromoValid] = React.useState(null);
  const [promoLoading, setPromoLoading] = React.useState(false);
  const [airbnbUrl, setAirbnbUrl] = React.useState("");
  const [pendingUrl, setPendingUrl] = React.useState(false); // when code requires URL

  // Formule ladder (fixed supplements over the AI base price)
  const recommendedFormula = ["essentiel", "hygiene", "prestige"].includes(dossier.recommended_formula)
    ? dossier.recommended_formula
    : "hygiene";
  const FORMULES = [
    { code: "essentiel", supp: 0, feats: ["clean"] },
    { code: "hygiene", supp: 19, feats: ["clean", "sanitize"] },
    { code: "prestige", supp: 35, feats: ["clean", "sanitize", "perfume"] },
  ];
  const [formula, setFormula] = React.useState(recommendedFormula); // AI-recommended pre-selected
  const formulaSupp = FORMULES.find((f) => f.code === formula)?.supp || 0;
  const chooseFormula = (code) => {
    setFormula(code);
    setPromoValid(null); // re-validate promo since amount changed
  };

  const basePrice = Number(dossier.price_estimate_eur) || 0;
  // Separate subscription upsell + hide sanitize/odor (now baked into formules)
  const allUpsells = Array.isArray(dossier.upsell_suggestions) ? dossier.upsell_suggestions : [];
  const subscription = allUpsells.find((u) => u.code === "subscription") || null;
  const EXCLUDED_UPSELL = new Set(["subscription", "sanitize", "odor"]);
  const standardUpsells = allUpsells.filter((u) => !EXCLUDED_UPSELL.has(u.code));
  const upsellTotal = selectedUpsells.reduce((s, u) => s + (Number(u.price_add_eur) || 0), 0);
  const subTotal = basePrice + formulaSupp + upsellTotal;
  const discount = promoValid?.discount_eur || 0;
  const finalPrice = Math.max(0, subTotal - discount);

  const toggleUpsell = (u) => {
    setSelectedUpsells((prev) =>
      prev.find((x) => x.code === u.code)
        ? prev.filter((x) => x.code !== u.code)
        : [...prev, u]
    );
    setPromoValid(null); // re-validate promo since amount changed
  };

  const applyPromo = async () => {
    const code = (promoCode || "").trim();
    if (!code) return;
    // Detect if user is subscribing (upsell subscription selected)
    const hasSubscription = !!selectedUpsells.find((u) => u.code === "subscription");
    const context = hasSubscription ? "subscription_only" : "devis";
    setPromoLoading(true);
    try {
      const r = await import("@/lib/api").then((m) => m.api.post("/promo/validate", {
        code,
        amount_eur: subTotal,
        context,
        airbnb_url: airbnbUrl,
      }));
      setPromoValid(r.data);
      setPendingUrl(false);
      const { toast } = await import("sonner");
      toast.success(`${r.data.label} · -${r.data.discount_eur} €`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Code invalide";
      const { toast } = await import("sonner");
      if (detail.toLowerCase().includes("airbnb") || detail.toLowerCase().includes("annonce")) {
        setPendingUrl(true);
        toast.info("Ajoute le lien de ton annonce Airbnb ci-dessous puis re-clique sur Appliquer");
      } else {
        setPromoValid(null);
        toast.error(detail);
      }
    } finally {
      setPromoLoading(false);
    }
  };

  // Human-readable label lookups
  const map = t("devis_v2.labels_map", { returnObjects: true }) || {};
  const humanTextile = map.textile?.[dossier.textile_type] || dossier.textile_type || "—";
  const humanMaterial = map.material?.[dossier.fabric_material] || dossier.fabric_material || "—";
  const humanRisk = map.risk?.[risk] || risk;
  const humanStains = (dossier.stain_types || []).map((s) => map.stains?.[s] || s).join(", ") || "—";

  const [showPpiTip, setShowPpiTip] = React.useState(false);

  const kpis = [
    { icon: Sparkles, label: t("devis.kpi.success"), value: `${successPct} %`, color: "text-emerald-600" },
    { icon: Clock, label: t("devis.kpi.time"), value: `${dossier.estimated_time_minutes ?? "—"} min`, color: "text-slate-700" },
  ];

  return (
    <div id="devis-result" className="mt-8 card-clean p-6 border-2 border-[#5BA4D4]/30" data-testid="devis-result">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#5BA4D4]/10 text-[#5BA4D4] flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <h2 className="font-semibold text-navy">{t("devis.dossier_title")}</h2>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${confClass}`} data-testid="devis-confidence">
          {conf}% · {t(`devis_v2.confidence_${confKey}`)}
        </span>
      </div>

      {/* HERO: Prezzo dinamico (base + upsell − sconto) */}
      <div className="mt-5 rounded-2xl bg-gradient-to-br from-navy to-navy/90 text-white p-6 relative overflow-hidden" data-testid="devis-hero-price">
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">
          {t("devis_v2.price_ai")}
        </div>
        {discount > 0 ? (
          <div className="mt-2 text-white/60 text-sm line-through" data-testid="devis-hero-strike">
            €{subTotal}
          </div>
        ) : null}
        <div className="mt-1 flex items-baseline gap-3 flex-wrap">
          <span className="text-5xl font-bold" data-testid="devis-price">€{finalPrice}</span>
          <span className="text-sm text-white/70">
            {t("devis_v2.price_precision")} ±{dossier.price_precision_percent ?? 15}%
          </span>
        </div>
        <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-white/80" data-testid="devis-hero-formula">
          <Award className="h-3.5 w-3.5 text-yellow-300" />
          {t("devis_v2.formules.chosen")} · <b>{t(`devis_v2.formules.${formula}_name`)}</b>
        </div>
        {discount > 0 ? (
          <div className="mt-2 inline-block bg-yellow-400 text-navy text-xs font-bold px-2 py-1 rounded" data-testid="devis-promo-applied">
            -€{discount} · {promoValid.code}
          </div>
        ) : null}
        <p className="mt-3 text-xs text-white/70 italic">{t("devis_v2.price_disclaimer")}</p>
      </div>

      {/* Formule ladder — scelta principale (Essentiel / Hygiène+ / Prestige) */}
      <div className="mt-5" data-testid="devis-formules">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-[#5BA4D4]" /> {t("devis_v2.formules.title")}
        </p>
        <p className="text-xs text-slate-600 mb-4 italic">{t("devis_v2.formules.intro")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FORMULES.map((f) => {
            const isSel = formula === f.code;
            const price = basePrice + f.supp;
            return (
              <button
                key={f.code}
                onClick={() => chooseFormula(f.code)}
                className={`relative text-left rounded-xl border-2 p-4 transition ${
                  isSel
                    ? "border-[#5BA4D4] bg-[#5BA4D4]/10 shadow-md"
                    : "border-slate-200 bg-white hover:border-[#5BA4D4]/50"
                }`}
                data-testid={`devis-formule-${f.code}`}
              >
                {f.code === recommendedFormula ? (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] bg-yellow-400 text-navy px-2 py-0.5 rounded-full font-bold uppercase tracking-wide shadow">
                    {t("devis_v2.formules.recommended")}
                  </span>
                ) : null}
                <div className="flex items-center justify-between">
                  <span className="font-bold text-navy text-sm">{t(`devis_v2.formules.${f.code}_name`)}</span>
                  <span
                    className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSel ? "border-[#5BA4D4] bg-[#5BA4D4]" : "border-slate-300"
                    }`}
                  >
                    {isSel ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                  </span>
                </div>
                <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{t(`devis_v2.formules.${f.code}_desc`)}</p>
                <ul className="mt-2 space-y-1">
                  {f.feats.map((ft) => (
                    <li key={ft} className="flex items-start gap-1.5 text-[11px] text-navy">
                      <ShieldCheck className="h-3 w-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{t(`devis_v2.formules.feat_${ft}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 pt-2 border-t border-slate-200 flex items-baseline gap-2 flex-wrap">
                  {f.code === "prestige" ? (
                    <span className="text-xs text-slate-400 line-through">€{basePrice + 40}</span>
                  ) : null}
                  <span className="text-xl font-bold text-navy">€{price}</span>
                  {f.supp > 0 ? <span className="text-[10px] text-slate-500">(+€{f.supp})</span> : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4 passaggi tecnici — cosa include il prezzo */}
      <div className="mt-5 rounded-2xl bg-white border-2 border-[#5BA4D4]/20 p-5" data-testid="devis-tech-steps">
        <p className="text-[11px] uppercase tracking-widest text-[#5BA4D4] font-bold mb-4 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> {t("devis_v2.steps_title")}
        </p>
        <ol className="space-y-3">
          {[1, 2, 3, 4].map((n) => (
            <li key={n} className="flex gap-3">
              <span className="flex-shrink-0 h-7 w-7 rounded-full bg-[#5BA4D4] text-white text-xs font-bold flex items-center justify-center">
                {n}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy">{t(`devis_v2.step_${n}_title`)}</p>
                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{t(`devis_v2.step_${n}_desc`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Upsell cards (excluding subscription — it's shown separately below) */}
      {standardUpsells.length > 0 ? (
        <div className="mt-4" data-testid="devis-upsell">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-[#5BA4D4]" /> {t("devis_v2.upsell_title")}
          </p>
          <p className="text-xs text-slate-600 mb-3 italic">{t("devis_v2.options_intro")}</p>
          <div className="space-y-2">
            {standardUpsells.map((u) => {
              const isSelected = !!selectedUpsells.find((x) => x.code === u.code);
              return (
                <button
                  key={u.code}
                  onClick={() => toggleUpsell(u)}
                  className={`w-full text-left rounded-xl border-2 transition p-3 ${
                    isSelected
                      ? "border-[#5BA4D4] bg-[#5BA4D4]/10"
                      : "border-slate-200 bg-white hover:border-[#5BA4D4]/50"
                  }`}
                  data-testid={`devis-upsell-${u.code}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {u.priority === "high" ? (
                          <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold uppercase">{t("devis_v2.upsell_hot")}</span>
                        ) : null}
                        <span className="font-semibold text-navy text-sm">{u.label}</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">{u.description}</p>
                      {u.why_recommended ? (
                        <p className="text-[11px] text-[#5BA4D4] mt-1 italic">💡 {u.why_recommended}</p>
                      ) : null}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-navy">+€{u.price_add_eur}</p>
                      {isSelected ? (
                        <span className="text-[10px] text-emerald-600 font-semibold">✓ Ajouté</span>
                      ) : (
                        <span className="text-[10px] text-slate-400">+ Ajouter</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Codice promo VOISIN20 */}
      <div className="mt-4 rounded-xl bg-yellow-50 border border-yellow-200 p-3" data-testid="devis-promo">
        <p className="text-[11px] uppercase tracking-widest text-yellow-700 font-semibold mb-2 flex items-center gap-1">
          🎁 {t("devis_v2.promo_title")}
        </p>
        {!promoValid ? (
          <div className="flex gap-2">
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="VOISIN20"
              maxLength={20}
              className="flex-1 rounded-lg border border-yellow-300 px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-yellow-300 outline-none"
              data-testid="devis-promo-input"
            />
            <button
              onClick={applyPromo}
              disabled={promoLoading || !promoCode || subTotal < 80}
              className="rounded-lg bg-yellow-400 hover:bg-yellow-300 text-navy font-semibold text-sm px-4 disabled:opacity-40"
              data-testid="devis-promo-apply"
            >
              {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Appliquer"}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="font-semibold text-navy">✓ {promoValid.label}</p>
              <p className="text-xs text-yellow-700">-€{promoValid.discount_eur} appliqués</p>
            </div>
            <button
              onClick={() => { setPromoValid(null); setPromoCode(""); }}
              className="text-xs text-yellow-700 hover:underline"
              data-testid="devis-promo-remove"
            >
              Retirer
            </button>
          </div>
        )}
        {subTotal < 80 && !promoValid ? (
          <p className="mt-1 text-[10px] text-yellow-700">
            {t("devis_v2.promo_min")}
          </p>
        ) : null}
        {subTotal >= 80 && !promoValid ? (
          <p className="mt-1 text-[10px] text-yellow-700 italic">
            💡 {t("devis_v2.promo_hint")}
          </p>
        ) : null}

        {pendingUrl && !promoValid ? (
          <div className="mt-3 pt-3 border-t border-yellow-300" data-testid="devis-airbnb-url-wrap">
            <label className="text-[10px] uppercase tracking-wider text-yellow-800 font-semibold">
              Lien de ton annonce Airbnb / location
            </label>
            <input
              value={airbnbUrl}
              onChange={(e) => setAirbnbUrl(e.target.value)}
              placeholder="https://www.airbnb.com/rooms/…"
              className="mt-1 w-full rounded-lg border border-yellow-300 px-3 py-2 text-sm"
              data-testid="devis-airbnb-url"
            />
          </div>
        ) : null}
      </div>

      {/* PPI Branded */}
      <div className="mt-4 rounded-xl border border-[#5BA4D4]/30 bg-[#5BA4D4]/5 p-4" data-testid="devis-ppi">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-[#5BA4D4]" />
              <span className="text-[11px] uppercase tracking-widest text-[#5BA4D4] font-semibold">
                {t("devis_v2.ppi_full")}
              </span>
            </div>
            <p className="text-4xl font-bold text-navy mt-1">
              {dossier.ppi_performance_index ?? "—"}<span className="text-lg text-slate-400">/100</span>
            </p>
          </div>
          <button
            onClick={() => setShowPpiTip((v) => !v)}
            className="text-xs text-[#5BA4D4] hover:underline flex items-center gap-1 self-start"
            data-testid="devis-ppi-how"
          >
            <Info className="h-3 w-3" /> {t("devis_v2.ppi_how")}
          </button>
        </div>
        {showPpiTip ? (
          <p className="mt-2 text-xs text-slate-600 leading-relaxed border-t border-[#5BA4D4]/20 pt-2">
            {t("devis_v2.ppi_tooltip")}
          </p>
        ) : null}
        {Array.isArray(dossier.ppi_breakdown) && dossier.ppi_breakdown.length > 0 ? (
          <div className="mt-3 space-y-1.5" data-testid="devis-ppi-breakdown">
            {dossier.ppi_breakdown.map((b, i) => {
              const positive = (b.delta ?? 0) >= 0;
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`inline-block w-10 text-right font-bold ${positive ? "text-emerald-600" : "text-red-500"}`}>
                    {positive ? "+" : ""}{b.delta}
                  </span>
                  <span className="text-navy font-medium">{b.factor}</span>
                  {b.explanation ? <span className="text-slate-500 hidden sm:inline">— {b.explanation}</span> : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Barra probabilità successo */}
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="devis-success">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            {t("devis.kpi.success")}
          </span>
          <span className="text-xl font-bold text-emerald-600">{successPct}%</span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className={`h-3 flex-1 rounded ${n <= successBars ? "bg-emerald-500" : "bg-slate-200"}`}
            />
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-600">
          {t(`devis_v2.success_${successKey}`)}
        </p>
      </div>

      {/* KPI compatti */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {kpis.map((k, i) => (
          <div key={i} className="rounded-xl bg-slate-50 p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <k.icon className={`h-3 w-3 ${k.color}`} /> {k.label}
            </div>
            <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Analisi in linguaggio umano */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Datum label={t("devis.field.textile")} value={humanTextile} />
        <Datum label={t("devis.field.material")} value={humanMaterial} />
        <Datum label={t("devis.field.colour")} value={dossier.colour_family} />
        <Datum label={t("devis.field.surface")} value={`${dossier.estimated_surface_m2 ?? "—"} m²`} />
        <Datum label={t("devis.field.dirt_level")} value={`${dossier.dirt_level ?? "—"}/5`} />
        <Datum label={t("devis.field.risk")} value={humanRisk} valueClass={riskColor} />
        <Datum label={t("devis.field.stains")} value={humanStains} full />
      </div>

      {/* Motivo del prezzo */}
      {dossier.price_reasoning || (Array.isArray(dossier.price_breakdown) && dossier.price_breakdown.length > 0) ? (
        <div className="mt-5 p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="devis-price-reasoning">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t("devis_v2.price_reasoning_title")}</p>
          {Array.isArray(dossier.price_breakdown) && dossier.price_breakdown.length > 0 ? (
            <ul className="space-y-1 mb-2">
              {dossier.price_breakdown.map((b, i) => (
                <li key={i} className="flex justify-between text-sm">
                  <span className="text-navy">{b.label}</span>
                  <span className="text-navy font-medium">€{b.amount}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {dossier.price_reasoning ? (
            <p className="text-xs text-slate-600 italic">{dossier.price_reasoning}</p>
          ) : null}
        </div>
      ) : null}

      {/* Trattamento */}
      <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
        <p className="text-[10px] uppercase tracking-wider text-emerald-700 mb-1">{t("devis.treatment")}</p>
        <p className="text-sm text-navy">{dossier.recommended_treatment || "—"}</p>
      </div>

      {dossier.technical_notes ? (
        <div className="mt-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-[10px] uppercase tracking-wider text-amber-700 mb-1">{t("devis.notes")}</p>
          <p className="text-sm text-navy leading-relaxed">{dossier.technical_notes}</p>
        </div>
      ) : null}

      {/* Alternative : Abonnement (separato dal preventivo normale) */}
      {subscription ? (
        <div className="mt-6" data-testid="devis-subscription-alt">
          <div className="text-center mb-2">
            <span className="inline-block text-[11px] uppercase tracking-widest text-slate-400 bg-white px-3 py-1">
              {t("devis_v2.subscription_or")}
            </span>
          </div>
          {/* Banner giallo — offerta 2 visite gratis */}
          <div className="rounded-t-2xl bg-yellow-400 text-navy text-center py-2.5 px-4 font-bold text-sm shadow-md" data-testid="devis-subscription-freebadge">
            {t("devis_v2.subscription_free_badge")}
          </div>
          <div className="rounded-b-2xl border-2 border-t-0 border-yellow-400 bg-emerald-50 p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-bl-lg">
              {t("devis_v2.subscription_badge")}
            </div>
            <div className="flex items-center gap-2 text-emerald-700">
              <Award className="h-5 w-5" />
              <p className="font-bold text-lg">{subscription.label}</p>
            </div>
            <p className="text-sm text-slate-700 mt-2">{subscription.description}</p>

            <ul className="mt-3 space-y-1.5 text-sm">
              <li className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span><b>10 visites payées, 12 reçues</b> — 2 offertes sur 12 mois</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span><b>1ère visite -20 €</b> — code VOISIN20 offert</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span><b>Certificat d&apos;hygiène Pro-pre</b> — à afficher pour ton annonce Airbnb ou location</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span><b>Priorité RDV</b> — créneaux prioritaires sur agenda</span>
              </li>
            </ul>

            <div className="mt-4 flex items-baseline justify-between flex-wrap gap-2 border-t border-emerald-300 pt-3">
              <div>
                <p className="text-3xl font-bold text-navy">€{subscription.price_add_eur}</p>
                <p className="text-xs text-slate-600">{t("devis_v2.subscription_price_note")}</p>
              </div>
              <button
                onClick={() => window.open(`https://wa.me/32466132839?text=${encodeURIComponent(`Bonjour Claudio, je souhaite souscrire l'Abonnement Voisin Confiance à €${subscription.price_add_eur} (dossier ${dossier?._meta?.session_id || ""})`)}`, "_blank")}
                className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-5 py-2.5 flex items-center gap-2"
                data-testid="devis-subscribe"
              >
                {t("devis_v2.subscription_cta")} <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500 italic">
              💡 Code secret AIRBNB30 disponible pour les hôtes Airbnb / locations
            </p>
          </div>
        </div>
      ) : null}

      {/* CTAs */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onBook}
          className="btn-primary flex-1"
          data-testid="devis-book"
        >
          <Sparkles className="h-4 w-4" /> {t("devis.book_cta")}
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => window.location.reload()}
          className="btn-outline"
          data-testid="devis-restart"
        >
          <RefreshCw className="h-4 w-4" /> {t("devis.restart")}
        </button>
      </div>
    </div>
  );
}

function Datum({ label, value, full, valueClass }) {
  return (
    <div className={full ? "col-span-2 sm:col-span-4" : ""}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-medium mt-0.5 ${valueClass || "text-navy"}`}>{value || "—"}</p>
    </div>
  );
}
